<template>
    <div class="plotly-print-guard">
        <v-hover v-if="showExport">
            <v-card-text slot-scope="{ hover }" class="caption grey--text">
                <v-layout style="min-height:40px;">
                    <v-spacer></v-spacer>
                      <span v-show="hover" ma-0 pa-0>
                          <v-btn @click="downloadWithCSS" outline small flat round class="grey--text" ma-0 pa-0>
                              Export as PDF
                          </v-btn>
                          <v-btn @click="downloadCSV" outline small flat round class="grey--text" ma-0 pa-0>
                              Download CSV
                          </v-btn>
                      </span>
                </v-layout>
            </v-card-text>
        </v-hover>
        <div ref="container" class="vue-plotly"></div>
    </div>
</template>
<script>
import Plotly from 'plotly.js/dist/plotly'
import debounce from 'lodash/debounce'
import defaults from 'lodash/defaults'
import jsPDF from 'jspdf'
import {exportCSVFromDicts} from "@/tocsv.js"

const events = [
  'click',
  'hover',
  'unhover',
  'selecting',
  'selected',
  'restyle',
  'relayout',
  'autosize',
  'deselect',
  'doubleclick',
  'redraw',
  'animated',
  'afterplot'
]

const functions = [
  'restyle',
  'relayout',
  'update',
  'addTraces',
  'deleteTraces',
  'moveTraces',
  'extendTraces',
  'prependTraces',
  'purge'
]

const methods = functions.reduce((all, funcName) => {
  all[funcName] = function(...args) {
    return Plotly[funcName].apply(Plotly, [this.$refs.container].concat(args))
  }
  return all
}, {})

export default {
  props: {
    autoResize: Boolean,
    watchShallow: {type:Boolean, default: true},
      forceInit: {type:Boolean, default: false},
      disableResponsive:{type:Boolean,default:false},
      showExport:{type:Boolean,default:false},
    options: {
      type: Object
    },
    data: {
      type: Array
    },
    layout: {
      type: Object
    }
  },
  data() {
    return {
      internalLayout: {
        ...this.layout,
        datarevision: 1,
          paper_bgcolor:process.env.VUE_APP_THEME=='dark' ? 'rgba(0,0,0,0)' : null,
          font:process.env.VUE_APP_THEME=='dark' ? {
            color:"#FFFFFF"
          } : null
      },
        needsInit:true
    }
  },
  mounted() {
      if(!this.internalLayout.plot_bgcolor){
          this.internalLayout.plot_bgcolor=process.env.VUE_APP_THEME=='dark' ? 'rgba(0,0,0,0)': null
      }
    this.react()

    this.$watch('data', () => {
      this.internalLayout.datarevision++
      this.react()
    }, { deep: !this.watchShallow })

    this.$watch('options', this.react, { deep: !this.watchShallow })
    this.$watch('layout', this.relayout, { deep: !this.watchShallow })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.__resizeListener)
      if(this.__generalListeners){
          this.__generalListeners.forEach(obj => this.$refs.container.removeAllListeners(obj.fullName))
      }
    Plotly.purge(this.$refs.container)
  },
  methods: {
      getPercentages(array){
          let sum = array.reduce((sum, x) => sum + x);
          return array.map(x => ((x/sum)*100).toFixed(2))
      },
      downloadCSV () {
          let df={};

          if(this.data.length>1){
              let col1="Time";
              if(this.layout&&this.layout.xaxis&&this.layout.xaxis.title){
                  col1=this.layout.xaxis.title.text;
              }
              if(this.data[0].x[0]===this.data[1].x[0]&&this.data[0].x.length===this.data[1].x.length){
                  for(let item of this.data){
                      if(!df[col1]){
                          df[col1]=item.x;
                      }
                      df[item.name]=item.y
                  }
                  exportCSVFromDicts(df)
              } else {
                  for(let item of this.data){
                      let df={};
                      df[col1]=item.x;
                      df[item.name]=item.y
                      exportCSVFromDicts(df)
                  }

              }

          } else if (this.data[0].type==="pie") {
              df["Labels"]=this.data[0].labels
              df["Values"]=this.data[0].values
              df["Percentages"]=this.getPercentages(this.data[0].values)
              exportCSVFromDicts(df)
          } else {
              let col1="Time";
              let col2="Value"
              if(this.layout&&this.layout.xaxis&&this.layout.xaxis.title){
                  col1=this.layout.xaxis.title.text;
              }
              if(this.layout&&this.layout.yaxis&&this.layout.yaxis.title){
                  col2=this.layout.yaxis.title.text;
              }
              df[col1]=this.data[0].x
              df[col2]=this.data[0].y
              exportCSVFromDicts(df)
          }

      },
      async downloadWithCSS() {
          let doc = new jsPDF('', 'pt', 'a4');
          let img = null
          img =  await this.toImage()
          doc.addImage(img, 'JPEG', 0, 0,
              1.0*595.28,
              1.0*592.28/this.$refs.container.clientWidth*this.$refs.container.clientHeight
          )
          doc.save("export.pdf")
      },
    initEvents() {
      if (this.autoResize) {
        this.__resizeListener = () => {
          this.internalLayout.datarevision++
          debounce(this.react, 200)
        }
        window.addEventListener('resize', this.__resizeListener)
      }

      this.__generalListeners = events.map((eventName) => {
        return {
          fullName: 'plotly_' + eventName,
          handler: (...args) => {
            this.$emit.apply(this, [eventName].concat(args))
          }
        }
      })

      this.__generalListeners.forEach((obj) => {
        this.$refs.container.on(obj.fullName, obj.handler)
      })
    },
    ...methods,
    toImage(options) {
          this.internalLayout.font=null
      let el = this.$refs.container
      let opts = defaults(options, {
        format: 'png',
        width: el.clientWidth,
        height: el.clientHeight
      })

      return Plotly.toImage(this.$refs.container, opts)
    },
    downloadImage(options) {
      let el = this.$refs.container
      let opts = defaults(options, {
        format: 'png',
        width: el.clientWidth,
        height: el.clientHeight,
        filename: (el.layout.title || 'plot') + ' - ' + new Date().toISOString()
      })

      return Plotly.downloadImage(this.$refs.container, opts)
    },
    plot() {
      return Plotly.plot(this.$refs.container, this.data, this.internalLayout, this.getOptions())
    },
    getOptions() {
      let el = this.$refs.container
      let opts = this.options

      // if width/height is not specified for toImageButton, default to el.clientWidth/clientHeight
      if (!opts) opts = {}
      opts.responsive=!this.disableResponsive;
      if (!opts.toImageButtonOptions) opts.toImageButtonOptions = {}
      if (!opts.toImageButtonOptions.width) opts.toImageButtonOptions.width = el.clientWidth
      if (!opts.toImageButtonOptions.height) opts.toImageButtonOptions.height = el.clientHeight
      return opts
    },
    newPlot() {
      return Plotly.newPlot(this.$refs.container, this.data, this.internalLayout, this.getOptions())
    },
    react() {
       if(this.data.length||this.forceInit){

           Plotly.react(this.$refs.container, this.data, this.internalLayout, this.getOptions())
           if(this.needsInit){
               this.initEvents()
               this.needsInit=false;
           }
       }

    }
  }
}
</script>
<style>
    @media print
    {
        .plotly-print-guard{
            page-break-inside: avoid !important;
        }
    }
</style>
